import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { DiscoveryIndicators } from "@/components/discovery/DiscoveryIndicators";
import { AREAS } from "@/components/discovery/areas";

// Fallback only when the personalized message could not be generated.
export const fallbackMessage = () => [
  "Look at what we've already uncovered together — and we've only had a short conversation.",
  "There's more we could explore: a conversation with your Future Self, one year from now; a Daily Battle Plan with a few small steps you could begin today; and your Purpose Engine, to get clearer on what matters most to you.",
  "And we don't have to start over. I'll remember what you've shared, and we can keep building on it.",
  "I think there's a lot more here for us to discover. Are you ready to find out?",
];

// Ellie personally returns: typing, then her personalized paragraphs appear one by one.
export const EllieReturns = ({ paragraphs, found, discoveries = [], order = [], onShown }) => {
  const [minDone, setMinDone] = useState(false);
  const typing = !minDone || !paragraphs;
  useEffect(() => { const t = setTimeout(() => setMinDone(true), 1400); return () => clearTimeout(t); }, []);
  useEffect(() => { if (!typing) onShown?.(); }, [typing, onShown]);
  // Gentle sequential highlight of lit features, in the order Ellie mentions them (runs once).
  const [burst, setBurst] = useState(null);
  const played = useRef(false);
  useEffect(() => {
    if (typing || played.current) return;
    played.current = true;
    const seq = order.filter((a) => found.includes(a));
    const ts = seq.map((a, i) => setTimeout(() => setBurst({ area: a, id: `return-${a}` }), 1200 + i * 2300));
    return () => ts.forEach(clearTimeout);
  }, [typing, order, found]);

  return (
    <section data-testid="ellie-returns" className="mt-16">
      <div className="flex flex-col items-center text-center">
        <EllieOrb size={64} testId="ellie-returns-avatar" />
        <p className="eyebrow mt-4 text-[11px] text-teal-700">Ellie</p>
      </div>
      {found.length > 0 && <DiscoveryIndicators found={found} discoveries={discoveries} burst={burst} source="ellie_return" className="mt-4" />}
      <div className="fio-glass-strong fio-hairline relative mt-6 overflow-hidden rounded-[28px] px-6 py-6">
        {typing ? (
          <div className="flex items-center gap-1.5 py-2" data-testid="ellie-returns-typing">
            {[0, 1, 2].map((i) => (<span key={i} className="h-2 w-2 animate-bounce rounded-full bg-gradient-to-br from-teal-500 to-violet-500" style={{ animationDelay: `${i * 0.15}s` }} />))}
          </div>
        ) : (
          <div className="space-y-3.5" data-testid="ellie-returns-message">
            {paragraphs.map((p, i) => (
              <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.45, ease: [0.22, 1, 0.36, 1] }}
                className={`text-[15.5px] leading-relaxed ${i === 0 || i === paragraphs.length - 1 ? "font-medium text-slate-900" : "text-slate-700"}`}>
                {p}
              </motion.p>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
